import { createFileRoute } from "@tanstack/react-router";

import { SiteLayout } from "@/components/SiteLayout";

const TITLE = "Política de Privacidade — TrajetoFácil";
const DESCRIPTION =
  "Política de privacidade do TrajetoFácil: quais dados coletamos, cookies, Google Analytics e Google AdSense, e seus direitos sobre as informações.";
const URL = "https://www.trajetofacil.com.br/privacidade";

export const Route = createFileRoute("/privacidade")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <SiteLayout>
      <article className="space-y-8 text-base leading-7 text-foreground">
        <header>
          <h1 className="text-3xl font-extrabold tracking-tight">Política de Privacidade</h1>
          <p className="mt-3 text-muted-foreground">
            Última atualização: setembro de 2026. Esta política explica quais informações o TrajetoFácil
            coleta, como elas são usadas e quais são os seus direitos.
          </p>
        </header>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">1. Dados que coletamos</h2>
          <p>
            <strong>Buscas de rotas:</strong> as origens, destinos e parâmetros informados na calculadora
            (tipo de veículo, preço do litro, consumo) são registrados de forma anônima para estatísticas
            de uso — por exemplo, quais cidades são mais buscadas — e para melhorar a base de tarifas.
            Não associamos essas buscas a nome, e-mail ou documento de identidade.
          </p>
          <p>
            <strong>Métricas de navegação:</strong> usamos o Google Analytics para medir visitas, páginas
            acessadas e interações de forma agregada, com um identificador anônimo de visitante.
          </p>
          <p>
            <strong>Feedback:</strong> mensagens enviadas pelo formulário de feedback ficam armazenadas em
            nosso painel interno, acessível apenas à equipe, para tratativa e melhoria da ferramenta.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">2. Cookies e anúncios</h2>
          <p>
            Utilizamos cookies e tecnologias semelhantes para funcionamento do site, mensuração de audiência
            e exibição de anúncios. O site exibe anúncios do <strong>Google AdSense</strong>. O Google, como
            fornecedor terceiro, utiliza cookies — incluindo o cookie DART — para veicular anúncios com base
            nas visitas anteriores do usuário a este e a outros sites.
          </p>
          <p>
            Você pode desativar a publicidade personalizada acessando as{" "}
            <a
              href="https://www.google.com/settings/ads"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary underline underline-offset-4"
            >
              Configurações de Anúncios do Google
            </a>
            . Para saber mais sobre como o Google usa dados de parceiros, visite{" "}
            <a
              href="https://policies.google.com/technologies/partner-sites?hl=pt-BR"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary underline underline-offset-4"
            >
              policies.google.com/technologies/partner-sites
            </a>
            .
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">3. Compartilhamento</h2>
          <p>
            Não vendemos dados pessoais. Compartilhamos informações apenas com os provedores necessários à
            operação do serviço: Google (Analytics e AdSense) e nossa infraestrutura de hospedagem e banco
            de dados, sempre em formato agregado ou operacional.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">4. Seus direitos</h2>
          <p>
            Nos termos da LGPD (Lei nº 13.709/2018), você pode solicitar informações sobre dados associados
            a você, correção ou exclusão. Como as buscas são anônimas e não exigem cadastro, em geral não
            conseguimos vincular registros a uma pessoa específica. Mensagens de feedback identificáveis
            podem ser excluídas mediante solicitação pelo canal de contato.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">5. Segurança e retenção</h2>
          <p>
            Os dados são armazenados em infraestrutura com criptografia em trânsito e em repouso. Métricas
            agregadas e feedback são mantidos pelo tempo necessário às finalidades descritas nesta política.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">6. Alterações desta política</h2>
          <p>
            Podemos atualizar esta política para refletir mudanças na ferramenta ou na legislação. A versão
            vigente estará sempre disponível nesta página, com a data da última atualização no topo.
          </p>
        </section>
      </article>
    </SiteLayout>
  );
}
